import { EmployerSignupStatus, Role } from '@zorro/clients';
import { SUCCESS_MESSAGES } from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Menu } from '@zorro/design-system/v2/Menu';
import { ModalMessage } from '@zorro/design-system/v2/ModalMessage';
import { ThreeDotsIcon } from '@zorro/shared/assets';
import {
  showSuccessNotification,
  useAppRouter,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

import { EmployerDeleteModal, EmployerRemoveEmployeeModal } from '.';

type Props = {
  employerId: string;
};

export const EmployerDotsMenu = ({ employerId }: Props) => {
  const { navigateToRoute } = useAppRouter();
  const { userRoles } = useRoles();
  const [open, setOpen] = useState(false);

  const { data: allEmployees = [], isLoading: isEmployeesLoading } =
    useMonolithQuery({
      method: 'employeesControllerFindAll',
      params: [employerId],
    });

  const shouldShowRemoveEmployeerFirst = allEmployees.length > 0;

  const [isDeleteEmployerModalOpen, setIsDeleteEmployerModalOpen] =
    useState<boolean>(false);
  const [isDeleteEmployeeFirstModalOpen, setIsDeleteEmployeeFirstModalOpen] =
    useState<boolean>(false);
  const [isChangeToProspectModalOpen, setIsChangeToProspectModalOpen] =
    useState<boolean>(false);

  const { mutate: mutateEmployer } = useMonolithMutation({
    method: 'employersControllerUpdate',
  });
  const { mutate: mutateEmployerDelete } = useMonolithMutation({
    successMessage: 'Deleted employer successfully.',
    method: 'employersControllerRemove',
    shouldReloadPage: false,
  });

  const { data: employer, isLoading } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: [employerId],
  });

  const showChangeToDemoOption =
    userRoles.includes(Role.OMNIPOTENT_ADMIN) ||
    userRoles.includes(Role.ACCOUNT_SUPERVISOR) ||
    userRoles.includes(Role.OPERATOR);

  const isDemoEmployer = employer?.isDemo || false;

  const handleChangeToProspect = async () => {
    try {
      await mutateEmployer([
        employerId,
        {
          zorroPartnerId: employer?.zorroPartnerId ?? null,
          producerId: employer?.producerId ?? null,
          signupStatus: EmployerSignupStatus.PROSPECT,
        },
      ]);
      showSuccessNotification({
        message: SUCCESS_MESSAGES.SET_EMPLOYER_TO_PROSPECT_SUCCESS_MESSAGE,
      });
      setIsChangeToProspectModalOpen(false);
      navigateToRoute('OPS_COMPANY_SETUP_PROSPECT', [employerId]);
    } catch {
      /* empty */
    }
  };

  const handleToggleDemoCompany = async () => {
    try {
      const {
        employer: { isDemo: newIsDemo },
      } = await mutateEmployer([
        employerId,
        {
          zorroPartnerId: employer?.zorroPartnerId ?? null,
          producerId: employer?.producerId ?? null,
          isDemo: !isDemoEmployer,
        },
      ]);

      showSuccessNotification({
        message: newIsDemo
          ? 'Marked as demo company'
          : 'Unmarked as demo company',
      });
    } catch {
      /* empty */
    }
  };

  const handleDeleteCompany = async () => {
    try {
      await mutateEmployerDelete([employerId]);
      if (employer?.isProspect) {
        navigateToRoute('OPS_PROSPECTS', []);
      } else {
        navigateToRoute('OPS_EMPLOYERS', []);
      }
    } catch {
      /* empty */
    }
  };

  if (!employer || isLoading) {
    return null;
  }

  const handleDeleteClick = () => {
    if (shouldShowRemoveEmployeerFirst) {
      setIsDeleteEmployeeFirstModalOpen(true);
    } else {
      setIsDeleteEmployerModalOpen(true);
    }
  };

  const handleNavigateToEmployees = () => {
    navigateToRoute('OPS_EMPLOYERS_EMPLOYEES', [{ employerId }]);
  };

  return (
    <>
      <Menu opened={open} onChange={setOpen} size="sm">
        <Menu.Target>
          <ActionIcon isChecked={open}>
            <ThreeDotsIcon />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {!employer?.isProspect && (
            <Menu.Item onClick={() => setIsChangeToProspectModalOpen(true)}>
              Change to prospect
            </Menu.Item>
          )}
          {showChangeToDemoOption && (
            <Menu.Item onClick={handleToggleDemoCompany}>
              {isDemoEmployer
                ? 'Unmark as demo company'
                : 'Mark as demo company'}
            </Menu.Item>
          )}
          <Menu.Item onClick={handleDeleteClick}>Delete employer</Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <ModalMessage
        isOpen={isChangeToProspectModalOpen}
        onClose={() => setIsChangeToProspectModalOpen(false)}
        variant="regular"
        title={`Change ${employer.name} to prospect?`}
        description="Are you sure you want to change this employer back to prospect mode?"
        primaryLabel="Change to prospect"
        onPrimaryAction={handleChangeToProspect}
        secondaryLabel="Cancel"
        onSecondaryAction={() => setIsChangeToProspectModalOpen(false)}
      />
      <EmployerDeleteModal
        isOpen={isDeleteEmployerModalOpen}
        setIsOpen={setIsDeleteEmployerModalOpen}
        employerName={employer?.name}
        onSubmit={handleDeleteCompany}
      />
      <EmployerRemoveEmployeeModal
        isOpen={isDeleteEmployeeFirstModalOpen}
        setIsOpen={setIsDeleteEmployeeFirstModalOpen}
        onSubmit={handleNavigateToEmployees}
        isLoading={isEmployeesLoading}
      />
    </>
  );
};
